import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';

export default function AboutPage() {
  const [activeTab, setActiveTab] = useState<'intro' | 'reputation' | 'contact'>('intro');

  const tabs = [
    { key: 'intro' as const, label: '品牌介绍', icon: 'ri-building-line' },
    { key: 'reputation' as const, label: '用户口碑', icon: 'ri-heart-line' },
    { key: 'contact' as const, label: '联系我们', icon: 'ri-mail-line' },
  ];

  return (
    <>
      <ScrollToTop />
      <Navbar />

      <main className="min-h-screen pt-20">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: '关于我们' },
            ]}
          />

          <h1 className="text-2xl md:text-3xl font-heading text-foreground-100 mb-8">
            关于牛牛影视
          </h1>

          <div className="flex items-center gap-1 mb-10 bg-background-100 rounded-full p-1 w-fit">
            {tabs.map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`flex items-center gap-2 px-5 py-2.5 text-sm rounded-full whitespace-nowrap transition-colors cursor-pointer ${
                  activeTab === tab.key
                    ? 'bg-primary-500 text-background-950 font-semibold'
                    : 'text-foreground-400 hover:text-foreground-200'
                }`}
              >
                <i className={tab.icon}></i>
                {tab.label}
              </button>
            ))}
          </div>

          {activeTab === 'intro' && (
            <section className="pb-14">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-start">
                <div>
                  <h2 className="text-xl font-heading text-foreground-100 mb-4">
                    品牌故事
                  </h2>
                  <p className="text-sm text-foreground-400 leading-relaxed mb-4">
                    牛牛影视创立于2020年，是一家专注于影视内容聚合与推荐的在线平台。我们的使命是让每一位观众都能轻松找到自己喜欢的高清影视内容，享受流畅舒适的观影体验。
                  </p>
                  <p className="text-sm text-foreground-400 leading-relaxed mb-4">
                    经过多年的发展，牛牛影视已积累了海量的电影、电视剧、综艺节目和动漫资源，涵盖动作、喜剧、剧情、科幻、悬疑、古装、仙侠等多种类型，满足不同年龄段、不同口味的观众需求。
                  </p>
                  <p className="text-sm text-foreground-400 leading-relaxed">
                    我们始终坚持"以用户为中心"的理念，不断优化产品体验，提升内容更新速度。无论是热播大剧还是经典老片，在牛牛影视都能找到，真正实现一站式追剧观影。
                  </p>

                  <div className="grid grid-cols-2 gap-4 mt-8">
                    {[
                      { num: '2万+', label: '影视资源' },
                      { num: '500万+', label: '日活用户' },
                      { num: '99.9%', label: '服务可用率' },
                      { num: '4.9', label: '用户评分' },
                    ].map((stat) => (
                      <div key={stat.label} className="bg-background-100 rounded-xl p-4 text-center">
                        <p className="text-2xl font-bold text-primary-400">{stat.num}</p>
                        <p className="text-xs text-foreground-500 mt-1">{stat.label}</p>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="relative">
                  <img
                    src="https://readdy.ai/api/search-image?query=Modern%20luxury%20home%20cinema%20room%20with%20large%20projector%20screen%2C%20comfortable%20red%20sofa%20seating%2C%20warm%20ambient%20lighting%2C%20soundproof%20walls%20with%20acoustic%20panels%2C%20elegant%20interior%20design%2C%20cozy%20atmosphere&width=800&height=600&seq=about-cinema-001&orientation=landscape"
                    alt="牛牛影视 - 品牌形象"
                    className="w-full rounded-xl object-cover"
                  />
                </div>
              </div>

              <div className="mt-14">
                <h2 className="text-xl font-heading text-foreground-100 mb-6 text-center">
                  我们的优势
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
                  {[
                    {
                      icon: 'ri-database-2-line',
                      title: '海量资源库',
                      desc: '聚合超过两万部影视作品，涵盖电影、电视剧、综艺、动漫等多种类型，持续更新中。',
                    },
                    {
                      icon: 'ri-speed-up-line',
                      title: '极速加载',
                      desc: '采用先进的CDN加速技术，确保高清视频流畅播放，告别缓冲等待。',
                    },
                    {
                      icon: 'ri-smartphone-line',
                      title: '多端兼容',
                      desc: '支持电脑、平板、手机等多种设备访问，随时随地享受观影乐趣。',
                    },
                    {
                      icon: 'ri-shield-check-line',
                      title: '安全可靠',
                      desc: '严格的内容审核机制和安全防护体系，为用户提供安心舒适的观影环境。',
                    },
                  ].map((item) => (
                    <div key={item.title} className="bg-background-100 rounded-xl p-6">
                      <div className="w-10 h-10 rounded-lg bg-accent-500/10 flex items-center justify-center mb-4">
                        <i className={`${item.icon} text-lg text-accent-400`}></i>
                      </div>
                      <h3 className="text-base font-semibold text-foreground-100 mb-2">
                        {item.title}
                      </h3>
                      <p className="text-xs text-foreground-500 leading-relaxed">
                        {item.desc}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            </section>
          )}

          {activeTab === 'reputation' && (
            <section className="pb-14">
              <h2 className="text-xl font-heading text-foreground-100 mb-2">
                用户口碑
              </h2>
              <p className="text-sm text-foreground-500 mb-8">
                来自真实用户的评价与反馈
              </p>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                {[
                  {
                    name: '小王',
                    role: '影视爱好者',
                    rating: 5,
                    content: '用了牛牛影视快三年了，资源真的很全，更新速度也快。追剧首选，画质清晰流畅，强烈推荐给喜欢看电影的朋友！',
                    avatar: 'https://readdy.ai/api/search-image?query=Professional%20headshot%20portrait%20of%20young%20Chinese%20man%20with%20friendly%20smile%2C%20clean%20white%20background%2C%20natural%20lighting%2C%20corporate%20style&width=100&height=100&seq=avatar-user-01&orientation=squarish',
                  },
                  {
                    name: '小李',
                    role: '大学生',
                    rating: 5,
                    content: '宿舍四个人都在用！综艺更新特别快，周末一起看综艺成了我们的固定节目。界面简洁好用，没有乱七八糟的广告。',
                    avatar: 'https://readdy.ai/api/search-image?query=Professional%20headshot%20portrait%20of%20young%20Chinese%20woman%20with%20warm%20smile%2C%20clean%20white%20background%2C%20natural%20lighting%2C%20casual%20style&width=100&height=100&seq=avatar-user-02&orientation=squarish',
                  },
                  {
                    name: '老张',
                    role: '退休教师',
                    rating: 5,
                    content: '退休后最大的爱好就是看电视剧，牛牛影视的资源分类很清晰，找剧很方便。古装剧和年代剧特别全，非常满意！',
                    avatar: 'https://readdy.ai/api/search-image?query=Professional%20headshot%20portrait%20of%20elderly%20Chinese%20man%20with%20kind%20smile%2C%20clean%20white%20background%2C%20natural%20lighting%2C%20warm%20expression&width=100&height=100&seq=avatar-user-03&orientation=squarish',
                  },
                  {
                    name: '小陈',
                    role: '程序员',
                    rating: 4,
                    content: '技术方面做得不错，加载速度快，画质好。动漫资源也挺全的，国漫和日漫都有。希望继续增加更多经典老片。',
                    avatar: 'https://readdy.ai/api/search-image?query=Professional%20headshot%20portrait%20of%20young%20Chinese%20tech%20worker%20with%20glasses%2C%20clean%20white%20background%2C%20natural%20lighting%2C%20smart%20casual&width=100&height=100&seq=avatar-user-04&orientation=squarish',
                  },
                  {
                    name: '阿花',
                    role: '全职妈妈',
                    rating: 5,
                    content: '带娃间隙看剧是我唯一的放松方式。牛牛影视的分类做得很好，能快速找到想看的类型。综艺和电视剧资源都很丰富！',
                    avatar: 'https://readdy.ai/api/search-image?query=Professional%20headshot%20portrait%20of%20Chinese%20woman%20in%2030s%20with%20gentle%20smile%2C%20clean%20white%20background%2C%20natural%20lighting%2C%20warm%20expression&width=100&height=100&seq=avatar-user-05&orientation=squarish',
                  },
                  {
                    name: '大刘',
                    role: '自媒体博主',
                    rating: 5,
                    content: '做影视解说需要大量素材，牛牛影视帮了我大忙。资源丰富、更新及时，分类导航特别方便。已经推荐给很多粉丝了！',
                    avatar: 'https://readdy.ai/api/search-image?query=Professional%20headshot%20portrait%20of%20Chinese%20man%20in%2030s%20with%20confident%20expression%2C%20clean%20white%20background%2C%20natural%20lighting%2C%20creative%20style&width=100&height=100&seq=avatar-user-06&orientation=squarish',
                  },
                ].map((review, i) => (
                  <div key={i} className="bg-background-100 rounded-xl p-5">
                    <div className="flex items-center gap-3 mb-3">
                      <img
                        src={review.avatar}
                        alt={review.name}
                        className="w-10 h-10 rounded-full object-cover"
                      />
                      <div>
                        <p className="text-sm font-semibold text-foreground-100">
                          {review.name}
                        </p>
                        <p className="text-xs text-foreground-500">{review.role}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-0.5 mb-3">
                      {Array.from({ length: 5 }).map((_, j) => (
                        <i
                          key={j}
                          className={`ri-star-fill text-xs ${
                            j < review.rating ? 'text-primary-400' : 'text-foreground-700'
                          }`}
                        ></i>
                      ))}
                    </div>
                    <p className="text-sm text-foreground-400 leading-relaxed">
                      "{review.content}"
                    </p>
                  </div>
                ))}
              </div>
            </section>
          )}

          {activeTab === 'contact' && (
            <section className="pb-14">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
                <div>
                  <h2 className="text-xl font-heading text-foreground-100 mb-2">
                    联系我们
                  </h2>
                  <p className="text-sm text-foreground-500 mb-8">
                    有任何问题或建议，欢迎随时与我们联系
                  </p>

                  <div className="space-y-4">
                    {[
                      { icon: 'ri-mail-line', label: '电子邮箱', value: 'contact@niuniutv.com' },
                      { icon: 'ri-wechat-line', label: '微信公众号', value: '牛牛影视官方' },
                      { icon: 'ri-weibo-line', label: '官方微博', value: '@牛牛影视' },
                      { icon: 'ri-customer-service-2-line', label: '客服热线', value: '400-888-6666' },
                    ].map((item) => (
                      <div key={item.label} className="flex items-center gap-4 p-4 bg-background-100 rounded-xl">
                        <div className="w-10 h-10 rounded-lg bg-primary-500/10 flex items-center justify-center flex-shrink-0">
                          <i className={`${item.icon} text-lg text-primary-400`}></i>
                        </div>
                        <div>
                          <p className="text-xs text-foreground-500 mb-0.5">{item.label}</p>
                          <p className="text-sm font-medium text-foreground-200">{item.value}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="bg-background-100 rounded-xl p-6">
                  <h3 className="text-lg font-heading text-foreground-100 mb-4">
                    给我们留言
                  </h3>
                  <form className="space-y-4" onSubmit={(e) => e.preventDefault()}>
                    <div>
                      <label className="block text-xs text-foreground-400 mb-1.5">
                        姓名
                      </label>
                      <input
                        type="text"
                        className="w-full px-4 py-2.5 text-sm bg-background-200 border border-background-300 rounded-lg text-foreground-200 placeholder:text-foreground-600 focus:outline-none focus:border-primary-400 transition-colors"
                        placeholder="请输入您的姓名"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-foreground-400 mb-1.5">
                        邮箱
                      </label>
                      <input
                        type="email"
                        className="w-full px-4 py-2.5 text-sm bg-background-200 border border-background-300 rounded-lg text-foreground-200 placeholder:text-foreground-600 focus:outline-none focus:border-primary-400 transition-colors"
                        placeholder="请输入您的邮箱"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-foreground-400 mb-1.5">
                        留言内容
                      </label>
                      <textarea
                        rows={4}
                        className="w-full px-4 py-2.5 text-sm bg-background-200 border border-background-300 rounded-lg text-foreground-200 placeholder:text-foreground-600 focus:outline-none focus:border-primary-400 transition-colors resize-none"
                        placeholder="请输入您的留言内容..."
                      ></textarea>
                    </div>
                    <button
                      type="submit"
                      className="w-full px-6 py-2.5 bg-primary-500 text-background-950 rounded-lg font-semibold text-sm whitespace-nowrap hover:bg-primary-400 transition-colors cursor-pointer"
                    >
                      提交留言
                    </button>
                  </form>
                </div>
              </div>
            </section>
          )}

          <section className="pb-14">
            <h2 className="text-xl font-heading text-foreground-100 mb-6">
              常见问题
            </h2>
            <div className="space-y-3">
              {[
                {
                  q: '牛牛影视是否需要注册才能观看？',
                  a: '不需要注册。牛牛影视对所有用户免费开放，您可以随时访问并浏览全部影视内容，无需注册账号。',
                },
                {
                  q: '牛牛影视的资源多久更新一次？',
                  a: '我们的内容团队每日更新资源，热门剧集和综艺节目同步更新，确保您第一时间观看到最新内容。',
                },
                {
                  q: '观看视频需要安装插件吗？',
                  a: '不需要。牛牛影视采用先进的HTML5播放技术，在主流浏览器上均可直接播放，无需安装任何插件。',
                },
                {
                  q: '支持哪些设备观看？',
                  a: '牛牛影视支持电脑（Windows/Mac）、平板（iPad/Android）、手机（iOS/Android）等多种设备，自动适配屏幕尺寸。',
                },
                {
                  q: '如何反馈问题或提出建议？',
                  a: '您可以通过联系我们页面的邮箱或在线留言功能反馈问题和建议，我们会在24小时内回复。',
                },
              ].map((faq, i) => (
                <details key={i} className="bg-background-100 rounded-xl group">
                  <summary className="flex items-center justify-between p-4 cursor-pointer text-sm font-medium text-foreground-200">
                    {faq.q}
                    <i className="ri-arrow-down-s-line text-lg text-foreground-500 group-open:rotate-180 transition-transform"></i>
                  </summary>
                  <div className="px-4 pb-4">
                    <p className="text-sm text-foreground-400 leading-relaxed">
                      {faq.a}
                    </p>
                  </div>
                </details>
              ))}
            </div>
          </section>
        </div>
      </main>

      <Footer />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'AboutPage',
            name: '关于牛牛影视',
            description: '牛牛影视品牌介绍、用户口碑和联系方式。',
            url: 'https://oil-zero.cn/about',
            isPartOf: {
              '@type': 'WebSite',
              name: '牛牛影视',
              url: 'https://oil-zero.cn',
            },
          }),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            mainEntity: [
              {
                '@type': 'Question',
                name: '牛牛影视是否需要注册才能观看？',
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: '不需要注册。牛牛影视对所有用户免费开放，您可以随时访问并浏览全部影视内容，无需注册账号。',
                },
              },
              {
                '@type': 'Question',
                name: '牛牛影视的资源多久更新一次？',
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: '我们的内容团队每日更新资源，热门剧集和综艺节目同步更新，确保您第一时间观看到最新内容。',
                },
              },
              {
                '@type': 'Question',
                name: '观看视频需要安装插件吗？',
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: '不需要。牛牛影视采用先进的HTML5播放技术，在主流浏览器上均可直接播放，无需安装任何插件。',
                },
              },
              {
                '@type': 'Question',
                name: '支持哪些设备观看？',
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: '牛牛影视支持电脑、平板、手机等多种设备，自动适配屏幕尺寸。',
                },
              },
              {
                '@type': 'Question',
                name: '如何反馈问题或提出建议？',
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: '您可以通过联系我们页面的邮箱或在线留言功能反馈问题和建议，我们会在24小时内回复。',
                },
              },
            ],
          }),
        }}
      />
    </>
  );
}